import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { AutomationRoundDTO, ProjectDetailDTO } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { Chip, EmptyState, Panel, ProgressBar, SectionHeader } from "@/design/primitives";
import { relativeTime } from "@/lib/format";
import type { ProjectTabKey } from "../ProjectDetailPage";

/**
 * OVERVIEW = AUTOMATIKA (artboard p2a).
 *
 * Obrazovka odpovídá na čtyři otázky v pořadí, v jakém je člověk klade:
 * **kde projekt je** (kaskáda šesti kol), **co ho drží** (brány),
 * **kdy bude hotový** (podmínky factory-ready) a **co se právě děje**.
 *
 * Dřív tu byly panely s poznámkou „Fáze 3" a odkazy na moduly — informaci
 * o tom, proč projekt stojí, nedávaly.
 */
export function OverviewTab({
  project,
  goTab,
}: {
  project: ProjectDetailDTO;
  goTab: (key: ProjectTabKey) => void;
}) {
  const automation = useQuery({
    queryKey: ["section", project.id, "automation"],
    queryFn: () => api.projectAutomation(project.id),
    refetchInterval: 30_000,
  });
  const events = useQuery({
    queryKey: ["audit", "project", project.id],
    queryFn: () => api.audit({ projectId: project.id, limit: 8 }),
  });
  const inbox = useQuery({
    queryKey: ["inbox", project.id],
    queryFn: () => api.decisionInbox(project.id),
  });

  const state = automation.data;
  const waiting = inbox.data?.items ?? [];
  const openGates = state?.gates.filter((g) => !g.ok) ?? [];
  const readiness = state?.readiness;

  return (
    <div className="mt-4 grid grid-cols-1 items-start gap-4 xl:grid-cols-[1.05fr_1fr_0.9fr]">
      {/* 1 — kde projekt je a co ho drží */}
      <div className="flex flex-col gap-4">
        <Panel className="p-5" data-testid="overview-automation">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <SectionHeader label={cs.project.automation} sub={cs.project.automationRounds} />
            {state ? (
              <span
                className="af-mono text-[10px] tracking-[0.14em]"
                style={{ color: openGates.length ? "var(--critical)" : "var(--positive)" }}
              >
                {openGates.length ? `■ ${cs.project.standsOnGate}` : `✓ ${cs.project.cascadeRuns}`}
              </span>
            ) : null}
          </div>

          <div className="mt-4 flex flex-col gap-3">
            {(state?.rounds ?? []).map((round) => (
              <Round key={round.key} round={round} slug={project.slug} />
            ))}
            {!state ? (
              <div className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>
                {cs.common.loading}
              </div>
            ) : null}
          </div>
        </Panel>

        <Panel
          tone={openGates.length ? "red" : "green"}
          className="p-5"
          data-testid="overview-gates"
        >
          <SectionHeader
            label={cs.project.whatHolds}
            tone={openGates.length ? "red" : "green"}
            sub={
              state
                ? `// ${state.gates.length - openGates.length} ${cs.project.ofGates} ${state.gates.length}`
                : undefined
            }
          />
          <div className="mt-3 flex flex-col gap-2.5">
            {openGates.map((gate) => (
              <div key={gate.key} className="flex gap-2.5 text-[13px]">
                <span style={{ color: "var(--critical)" }}>⛔</span>
                <span style={{ color: "var(--text-primary)" }}>{gate.detail ?? gate.label}</span>
              </div>
            ))}
            {(state?.gates ?? [])
              .filter((g) => g.ok)
              .map((gate) => (
                <div key={gate.key} className="flex gap-2.5 text-[12px]">
                  <span style={{ color: "var(--positive)" }}>✓</span>
                  <span style={{ color: "var(--text-tertiary)" }}>{gate.label}</span>
                </div>
              ))}
          </div>
        </Panel>
      </div>

      {/* 2 — kdy to bude hotové a co běží */}
      <div className="flex flex-col gap-4">
        <Panel tone="cyan" className="p-5" data-testid="overview-readiness">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="af-panel-title">{cs.project.whenReady}</span>
            <span className="af-mono text-[10px] tracking-[0.16em]" style={{ color: "var(--accent)" }}>
              {readiness?.factoryReadyAt ? `★ ${cs.project.factoryReady}` : "FACTORY-READY"}
            </span>
          </div>
          <div className="mt-1 text-[12px]" style={{ color: "var(--text-tertiary)" }}>
            {cs.project.readyConditions}
          </div>

          {readiness ? (
            <div className="mt-4 flex flex-col gap-3.5">
              <Condition
                label={cs.project.planTicked}
                value={`${readiness.planDone}/${readiness.planTotal}`}
                note={
                  readiness.planTotal
                    ? cs.project.planRemaining(readiness.planTotal - readiness.planDone)
                    : cs.project.planMissing
                }
                progress={
                  readiness.planTotal ? (readiness.planDone / readiness.planTotal) * 100 : 0
                }
                done={readiness.planTotal > 0 && readiness.planDone === readiness.planTotal}
              />
              <Condition
                label={cs.project.noOpenWork}
                value={`${readiness.openWork} ${cs.project.tasksUnit}`}
                note={readiness.inQa ? cs.project.inQa(readiness.inQa) : cs.project.qaEmpty}
                progress={readiness.openWork === 0 ? 100 : 0}
                done={readiness.openWork === 0}
              />
              <Condition
                label={cs.project.cleanTestRound}
                value={
                  readiness.lastTestFindings == null
                    ? "—"
                    : `${readiness.lastTestFindings} ${cs.project.findings}`
                }
                // netestováno není totéž co čisté — bez kola se podmínka neplní
                note={
                  readiness.lastTestFindings == null
                    ? cs.project.noTestRound
                    : readiness.lastTestFindings === 0
                      ? cs.project.testRoundClean
                      : cs.project.testRoundNote
                }
                progress={readiness.lastTestFindings === 0 ? 100 : 0}
                done={readiness.lastTestFindings === 0}
              />
            </div>
          ) : null}
        </Panel>

        <Panel className="p-5" data-testid="overview-running">
          <SectionHeader label={cs.project.nowRunning} />
          {state && state.running.length > 0 ? (
            <div className="mt-3 flex flex-col gap-3">
              {state.running.map((task) => (
                <Link
                  key={task.number}
                  to="/projects/$slug/tasks/$number"
                  params={{ slug: project.slug, number: String(task.number) }}
                  className="block"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[13px] font-semibold" style={{ color: "var(--text-primary)" }}>
                      #{task.number} {task.title}
                    </span>
                    <span className="af-mono shrink-0 text-[10px]" style={{ color: task.system ? "var(--info)" : "var(--accent)" }}>
                      {task.system ? "SYS" : `EP ${task.epScore}`}
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2">
                    <span className="af-mono text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                      {task.workerName ?? cs.project.unclaimed}
                    </span>
                    {task.progress != null ? (
                      <>
                        <ProgressBar value={task.progress} className="max-w-[120px] flex-1" />
                        <span className="af-mono text-[10px]" style={{ color: "var(--accent)" }}>
                          {task.progress} %
                        </span>
                      </>
                    ) : null}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState title={cs.project.nothingRuns} note={cs.project.nothingRunsNote} />
          )}
        </Panel>
      </div>

      {/* 3 — co čeká na člověka */}
      <div className="flex flex-col gap-4">
        <Panel tone={waiting.length ? "amber" : undefined} className="p-5" data-testid="overview-waiting">
          <SectionHeader
            label={cs.project.waitsForYou}
            tone={waiting.length ? "amber" : "cyan"}
            right={<Link to="/decisions">inbox →</Link>}
          />
          {waiting.length === 0 ? (
            <EmptyState title={cs.project.nothingWaits} />
          ) : (
            <div className="mt-3 flex flex-col gap-3">
              {waiting.slice(0, 4).map((item) => (
                <Link key={item.id} to="/decisions" className="block">
                  <Chip tone={item.urgency === "URGENT" ? "red" : "amber"}>{item.urgency}</Chip>
                  <div className="mt-1 text-[13px]" style={{ color: "var(--text-primary)" }}>
                    {item.title}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Panel>

        <Panel className="p-5" data-testid="overview-blocked">
          <SectionHeader
            label={`${cs.project.blockedTasks} · ${state?.blocked.length ?? 0}`}
            right={<button type="button" onClick={() => goTab("kanban")}>{cs.project.toBoard} →</button>}
          />
          {state && state.blocked.length > 0 ? (
            <div className="mt-3 flex flex-col gap-2">
              {state.blocked.map((task) => (
                <Link
                  key={task.number}
                  to="/projects/$slug/tasks/$number"
                  params={{ slug: project.slug, number: String(task.number) }}
                  className="flex items-baseline justify-between gap-3 text-[12px]"
                >
                  <span style={{ color: "var(--text-primary)" }}>
                    #{task.number} {task.title}
                  </span>
                  <span className="shrink-0" style={{ color: "var(--text-tertiary)" }}>
                    {task.reason}
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState title={cs.project.nothingBlocked} />
          )}
        </Panel>

        <Panel className="p-5">
          <SectionHeader
            label={cs.project.lastEvents}
            right={<button type="button" onClick={() => goTab("audit")}>audit →</button>}
          />
          <div className="mt-3 flex flex-col gap-2">
            {(events.data?.events ?? []).slice(0, 6).map((e) => (
              <div key={e.id} className="flex items-baseline justify-between gap-3 text-[12px]">
                <span className="min-w-0">
                  <span className="af-mono text-[10px]" style={{ color: "var(--accent)" }}>
                    {e.actorType}
                  </span>{" "}
                  <span style={{ color: "var(--text-secondary)" }}>{e.summary}</span>
                </span>
                <span className="af-mono shrink-0 text-[10px]" style={{ color: "var(--text-tertiary)" }}>
                  {relativeTime(e.ts)}
                </span>
              </div>
            ))}
            {(events.data?.events ?? []).length === 0 ? (
              <EmptyState title={cs.audit.empty} />
            ) : null}
          </div>
        </Panel>
      </div>
    </div>
  );
}

/** Řádek kaskády: pořadové číslo se stavem, název kola a jednou větou proč. */
function Round({ round, slug }: { round: AutomationRoundDTO; slug: string }) {
  const badge =
    round.state === "done"
      ? { text: "✓", bg: "var(--positive-bg)", color: "var(--positive-on-tint)" }
      : round.state === "running"
        ? { text: "▶", bg: "var(--accent-bg)", color: "var(--accent-on-tint)" }
        : round.state === "blocked"
          ? { text: String(round.order), bg: "var(--critical-bg)", color: "var(--critical-on-tint)" }
          : { text: String(round.order), bg: "var(--surface-inset)", color: "var(--text-tertiary)" };

  const body = (
    <div className="flex items-start gap-3">
      <span
        className="af-mono flex h-6 w-6 shrink-0 items-center justify-center text-[11px] font-bold"
        style={{ borderRadius: "var(--radius-chip)", background: badge.bg, color: badge.color }}
      >
        {badge.text}
      </span>
      <span className="min-w-0">
        <span
          className="block text-[13px]"
          style={{
            color: round.state === "waiting" ? "var(--text-secondary)" : "var(--text-primary)",
            fontWeight: round.state === "running" ? 700 : 500,
          }}
        >
          {round.title}
        </span>
        <span className="block text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          {round.note}
        </span>
      </span>
    </div>
  );

  return round.taskNumber ? (
    <Link to="/projects/$slug/tasks/$number" params={{ slug, number: String(round.taskNumber) }}>
      {body}
    </Link>
  ) : (
    body
  );
}

/** Jedna podmínka factory-ready s ukazatelem postupu. */
function Condition({
  label,
  value,
  note,
  progress,
  done,
}: {
  label: string;
  value: string;
  note: string;
  progress: number;
  done: boolean;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[13px]" style={{ color: "var(--text-primary)" }}>
          {done ? "✓ " : ""}
          {label}
        </span>
        <span
          className="af-mono shrink-0 text-[11px]"
          style={{ color: done ? "var(--positive)" : "var(--warning)" }}
        >
          {value}
        </span>
      </div>
      <ProgressBar value={progress} tone={done ? "green" : "cyan"} className="mt-1.5" />
      <div className="mt-1 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
        {note}
      </div>
    </div>
  );
}
